// frontend/src/hooks/useStaffDirectoryData.js
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';

/**
 * Hook to fetch staff directory data (all employees for directory view)
 */
export const useStaffDirectory = (params = {}) => {
  return useQuery({
    queryKey: ['staff', 'directory', params],
    queryFn: async () => {
      const response = await api.get('/staff/', { 
        params: { 
          page_size: 1000,
          view: 'directory',
          ...params 
        } 
      });
      return {
        employees: response.data?.results || response.data || [],
        count: response.data?.count || 0,
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch departments for filtering
 */
export const useDepartments = () => {
  return useQuery({
    queryKey: ['configuration', 'departments'],
    queryFn: async () => {
      const response = await api.get('/configuration/departments/');
      return response.data?.results || response.data || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes - departments rarely change
  });
};

/**
 * Combined hook for staff directory page
 */
export const useStaffDirectoryPage = (params = {}) => {
  const staffQuery = useStaffDirectory(params);
  const departmentsQuery = useDepartments();

  const employees = staffQuery.data?.employees || [];
  const apiCount = staffQuery.data?.count || 0;
  
  return {
    employees,
    totalEmployees: apiCount > 0 ? apiCount : employees.length,
    departments: departmentsQuery.data || [],
    isLoading: staffQuery.isLoading || departmentsQuery.isLoading,
    isError: staffQuery.isError || departmentsQuery.isError,
    error: staffQuery.error || departmentsQuery.error,
    refetch: () => {
      staffQuery.refetch();
      departmentsQuery.refetch();
    },
  };
};

export default {
  useStaffDirectory,
  useDepartments,
  useStaffDirectoryPage,
};
